import type { ApiClient } from '../client';
import type {
  ApiResponse,
  AddExamSubjectRequest,
  AddExamSubjectResponse,
  CreateExamRequest,
  CreateExamResponse,
  ListExamResultAuditsResponse,
  ListExamSubjectsResponse,
  ListExamsResponse,
  TeachingEffectivenessResponse,
} from '../types';
import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery, collectAllPages } from '../../../types/pagination';

function teachingEffectivenessQuery(params?: {
  academic_year?: string;
  term?: string;
}): string {
  const qs = new URLSearchParams();
  if (params?.academic_year?.trim()) qs.set('academic_year', params.academic_year.trim());
  if (params?.term?.trim()) qs.set('term', params.term.trim());
  const encoded = qs.toString();
  return encoded ? `?${encoded}` : '';
}

export function bindExams(client: ApiClient) {
  return {
    async listExams(params?: PaginationQuery): Promise<ApiResponse<ListExamsResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      const query = qs.toString();
      return client.request<ListExamsResponse>(query ? `/exams?${query}` : '/exams');
    },

    /** Every exam (follows pagination; default API page is only 50). */
    async listAllExams(): Promise<ListExamsResponse['exams']> {
      return collectAllPages(async (page, limit) => {
        const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
        const res = await client.request<ListExamsResponse>(`/exams?${qs}`);
        return {
          items: res.data?.exams ?? [],
          totalPages: res.data?.total_pages,
        };
      });
    },

    async listExamSubjects(params?: PaginationQuery): Promise<ApiResponse<ListExamSubjectsResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      return client.request<ListExamSubjectsResponse>(
        qs.toString() ? `/exams/subjects?${qs.toString()}` : '/exams/subjects'
      );
    },

    /** Every exam–subject link (schools exceed the default 20/50 page quickly). */
    async listAllExamSubjects(): Promise<ListExamSubjectsResponse['exam_subjects']> {
      return collectAllPages(async (page, limit) => {
        const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
        const res = await client.request<ListExamSubjectsResponse>(`/exams/subjects?${qs}`);
        return {
          items: res.data?.exam_subjects ?? [],
          totalPages: res.data?.total_pages,
        };
      });
    },

    async createExam(body: CreateExamRequest): Promise<ApiResponse<CreateExamResponse>> {
      return client.request<CreateExamResponse>('/exams', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async addExamSubject(body: AddExamSubjectRequest): Promise<ApiResponse<AddExamSubjectResponse>> {
      return client.request<AddExamSubjectResponse>('/exams/subjects', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /**
     * Admin: school-wide teacher effectiveness from student marks by subject.
     * Backend should aggregate published marks via teaching assignments.
     */
    async getTeachingEffectiveness(params?: {
      academic_year?: string;
      term?: string;
    }): Promise<ApiResponse<TeachingEffectivenessResponse>> {
      return client.request<TeachingEffectivenessResponse>(
        `/exams/analytics/teaching-effectiveness${teachingEffectivenessQuery(params)}`
      );
    },

    /** Admin: who changed which exam marks (from → to). */
    async listExamResultAudits(params?: {
      student_id?: string;
      exam_subject_id?: string;
      actor_user_id?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<ListExamResultAuditsResponse>> {
      const qs = new URLSearchParams();
      if (params?.student_id?.trim()) qs.set('student_id', params.student_id.trim());
      if (params?.exam_subject_id?.trim()) qs.set('exam_subject_id', params.exam_subject_id.trim());
      if (params?.actor_user_id?.trim()) qs.set('actor_user_id', params.actor_user_id.trim());
      appendPaginationQuery(qs, params);
      const query = qs.toString();
      return client.request<ListExamResultAuditsResponse>(
        query ? `/exams/results/audits?${query}` : '/exams/results/audits'
      );
    },
  };
}
